import React, { useEffect, useState } from "react";
import {
  Container, Card, CardBody, Row, Col, Label, Input, Button, Spinner,
  FormGroup, Alert, ListGroup, ListGroupItem, Badge, Table,
  Modal, ModalHeader, ModalBody, ModalFooter,
} from "reactstrap";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  getTenantById, updateTenant, getPortalById,
  uploadTenantDocument, getTenantDocuments, deleteTenantDocument,
  getPropertiesByOwner, getPropertyById, createProperty,
  getTicketsByTenantRecordId, deleteTenant, restoreTenant,
} from "../../../helpers/firestoreService";
import { useAuthUser } from "../../../hooks/AuthProvider";

const TenantDetail = () => {
  document.title = "Tenant Details | Support Portal";

  const { portalId, tenantId } = useParams();
  const navigate = useNavigate();
  const user = useAuthUser();
  const [tenant, setTenant] = useState(null);
  const [portal, setPortal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [propertyId, setPropertyId] = useState("");
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Properties
  const [properties, setProperties] = useState([]);
  const [propertiesLoading, setPropertiesLoading] = useState(false);

  // Documents
  const [documents, setDocuments] = useState([]);
  const [docsLoading, setDocsLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [docError, setDocError] = useState("");

  // Tickets
  const [tickets, setTickets] = useState([]);
  const [ticketsLoading, setTicketsLoading] = useState(true);

  // Delete / restore
  const [deleteModal, setDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [restoring, setRestoring] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [t, p] = await Promise.all([
          getTenantById(tenantId),
          getPortalById(portalId),
        ]);
        if (!t || !p) {
          navigate(`/tenants/${portalId}`);
          return;
        }
        setTenant(t);
        setPortal(p);
        setName(t.name || "");
        setEmail(t.email || "");
        setPropertyId(t.propertyId || "");
      } catch (e) {
        console.error("Load tenant error:", e);
      } finally {
        setLoading(false);
      }
    })();
  }, [tenantId, portalId, navigate]);

  useEffect(() => {
    if (!user) return;
    loadProperties();
  }, [user]);

  useEffect(() => {
    if (!tenantId) return;
    loadDocuments();
  }, [tenantId]);

  useEffect(() => {
    if (!portalId || !tenantId) return;
    (async () => {
      setTicketsLoading(true);
      try {
        setTickets(await getTicketsByTenantRecordId(tenantId));
      } catch (e) {
        console.error("Load tenant tickets error:", e);
      } finally {
        setTicketsLoading(false);
      }
    })();
  }, [portalId, tenantId]);

  const loadProperties = async () => {
    setPropertiesLoading(true);
    try {
      setProperties(await getPropertiesByOwner(user.uid));
    } catch (e) {
      console.error("Load properties error:", e);
    } finally {
      setPropertiesLoading(false);
    }
  };

  const loadDocuments = async () => {
    setDocsLoading(true);
    setDocError("");
    try {
      const docs = await getTenantDocuments(tenantId);
      setDocuments(docs);
    } catch (e) {
      console.error("Load documents error:", e);
      setDocError("Unable to load documents. Firebase Storage may not be configured.");
    } finally {
      setDocsLoading(false);
    }
  };

  const handleSave = async () => {
    if (!name.trim() || !email.trim()) return;
    setSaving(true);
    setSaveSuccess(false);
    try {
      await updateTenant(tenantId, {
        name: name.trim(),
        email: email.trim(),
        propertyId: propertyId || "",
      });
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (e) {
      console.error("Save tenant error:", e);
    } finally {
      setSaving(false);
    }
  };



  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    setDocError("");
    try {
      await uploadTenantDocument(tenantId, file);
      await loadDocuments();
    } catch (err) {
      console.error("Upload error:", err);
      setDocError("Upload failed. Firebase Storage may not be configured yet.");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const handleDeleteDoc = async (fileName) => {
    setDocError("");
    try {
      await deleteTenantDocument(tenantId, fileName);
      setDocuments((prev) => prev.filter((d) => d.name !== fileName));
    } catch (err) {
      console.error("Delete doc error:", err);
      setDocError("Failed to delete document.");
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await deleteTenant(tenantId);
      const refreshed = await getTenantById(tenantId);
      if (refreshed) setTenant(refreshed);
      setDeleteModal(false);
    } catch (e) {
      console.error("Delete tenant error:", e);
    } finally {
      setDeleting(false);
    }
  };

  const handleRestore = async () => {
    setRestoring(true);
    try {
      await restoreTenant(tenantId);
      const refreshed = await getTenantById(tenantId);
      if (refreshed) setTenant(refreshed);
    } catch (e) {
      console.error("Restore tenant error:", e);
    } finally {
      setRestoring(false);
    }
  };

  // Resolve the selected property's address for display
  const selectedProperty = properties.find((p) => p.id === propertyId);

  if (loading) {
    return (
      <div className="page-content text-center py-5">
        <Spinner color="primary" />
      </div>
    );
  }

  if (!tenant) return null;

  return (
    <div className="page-content">
      <Container fluid>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h4 className="mb-1">Tenant Details</h4>
            {portal && (
              <p className="text-muted mb-0">
                Portal: <Link to={`/portals/${portalId}`}>{portal.name}</Link>
                {" · "}
                <Link to={`/tenants/${portalId}`}>All Tenants</Link>
              </p>
            )}
          </div>
          <Button color="secondary" outline onClick={() => navigate(`/tenants/${portalId}`)}>
            <i className="bx bx-arrow-back me-1" /> Back to Tenants
          </Button>
        </div>

        {/* Deleted banner */}
        {tenant.deleted && (
          <Alert color="danger" className="mb-4 d-flex align-items-center justify-content-between">
            <span>
              <i className="bx bx-trash me-2" />
              <strong>This tenant has been deleted.</strong>{" "}
              Their portal access has been revoked. You can restore them at any time.
            </span>
            <Button color="danger" size="sm" disabled={restoring} onClick={handleRestore}>
              {restoring ? <Spinner size="sm" /> : <><i className="bx bx-undo me-1" />Restore</>}
            </Button>
          </Alert>
        )}

        <Row className="g-4">
          {/* Tenant Info */}
          <Col md={6}>
            <Card>
              <CardBody>
                <h5 className="mb-3">Tenant Information</h5>
                {saveSuccess && (
                  <Alert color="success" className="py-2">Saved successfully!</Alert>
                )}

                {/* Portal Badge */}
                {portal && (
                  <div className="mb-3">
                    <Label className="d-block">Portal</Label>
                    <Badge color="info" className="fs-6">
                      <i className="bx bx-globe me-1" />
                      {portal.name}
                    </Badge>
                  </div>
                )}

                <FormGroup>
                  <Label>Name *</Label>
                  <Input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Tenant name"
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Email *</Label>
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="tenant@example.com"
                  />
                  <small className="text-muted">This should match the email they use to log in.</small>
                </FormGroup>

                {/* Property Selection */}
                <FormGroup>
                  <Label>Property</Label>
                  <Input
                    type="select"
                    value={propertyId}
                    onChange={(e) => setPropertyId(e.target.value)}
                    disabled={propertiesLoading}
                  >
                    <option value="">— No property —</option>
                    {properties.map((p) => (
                      <option key={p.id} value={p.id}>{p.address}</option>
                    ))}
                  </Input>
                  {selectedProperty && (
                    <small className="text-muted">
                      <Link to={`/properties/${selectedProperty.id}`}>View property</Link>
                    </small>
                  )}
                </FormGroup>

                <div className="d-flex gap-2">
                  <Button
                    color="primary"
                    disabled={!name.trim() || !email.trim() || saving}
                    onClick={handleSave}
                  >
                    {saving ? <Spinner size="sm" /> : "Save Changes"}
                  </Button>
                  <Button
                    color="secondary"
                    onClick={() => navigate(`/tenants/${portalId}`)}
                  >
                    Cancel
                  </Button>
                  {!tenant.deleted && (
                    <Button
                      color="danger"
                      outline
                      size="sm"
                      className="ms-auto"
                      onClick={() => setDeleteModal(true)}
                    >
                      <i className="bx bx-trash" />
                    </Button>
                  )}
                </div>

                <div className="bg-light p-3 rounded mt-4">
                  <p className="mb-1">
                    <strong>Created:</strong>{" "}
                    {tenant.createdAt ? tenant.createdAt.toLocaleDateString() : "—"}
                  </p>
                  {tenant.updatedAt && (
                    <p className="mb-0">
                      <strong>Updated:</strong> {tenant.updatedAt.toLocaleDateString()}
                    </p>
                  )}
                </div>
              </CardBody>
            </Card>
          </Col>

          {/* Documents */}
          <Col md={6}>
            <Card>
              <CardBody>
                <h5 className="mb-3">Documents</h5>
                <p className="text-muted small mb-3">
                  Upload and manage documents for this tenant. Files are stored in Firebase Storage.
                </p>

                <FormGroup>
                  <Label for="docUpload" className="btn btn-outline-primary btn-sm mb-0">
                    {uploading ? <Spinner size="sm" className="me-1" /> : <i className="bx bx-upload me-1" />}
                    {uploading ? "Uploading..." : "Upload Document"}
                  </Label>
                  <Input
                    id="docUpload"
                    type="file"
                    className="d-none"
                    onChange={handleFileUpload}
                    disabled={uploading}
                  />
                </FormGroup>

                {docError && (
                  <Alert color="warning" className="py-2">{docError}</Alert>
                )}

                {docsLoading ? (
                  <div className="text-center py-3">
                    <Spinner size="sm" color="primary" />
                  </div>
                ) : documents.length === 0 ? (
                  <p className="text-muted small fst-italic">No documents uploaded yet.</p>
                ) : (
                  <ListGroup flush className="border rounded">
                    {documents.map((d) => (
                      <ListGroupItem key={d.name} className="d-flex justify-content-between align-items-center py-2">
                        <a
                          href={d.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-truncate me-2"
                        >
                          <i className="bx bx-file me-1" />
                          {d.name}
                        </a>
                        <Button
                          color="danger"
                          outline
                          size="sm"
                          onClick={() => handleDeleteDoc(d.name)}
                        >
                          <i className="bx bx-trash" />
                        </Button>
                      </ListGroupItem>
                    ))}
                  </ListGroup>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Tickets Section */}
        <Row className="mt-4">
          <Col>
            <Card>
              <CardBody>
                <h5 className="mb-3">
                  Tickets
                  {!ticketsLoading && (
                    <Badge color="primary" pill className="ms-2">{tickets.length}</Badge>
                  )}
                </h5>
                {ticketsLoading ? (
                  <div className="text-center py-3"><Spinner size="sm" color="primary" /></div>
                ) : tickets.length === 0 ? (
                  <p className="text-muted small fst-italic">No tickets for this tenant.</p>
                ) : (
                  <div className="table-responsive">
                    <Table className="table-hover mb-0" size="sm">
                      <thead className="table-light">
                        <tr>
                          <th>Title</th>
                          <th>Severity</th>
                          <th>Status</th>
                          <th>Property</th>
                          <th>Created</th>
                        </tr>
                      </thead>
                      <tbody>
                        {tickets.map((t) => (
                          <tr key={t.id}>
                            <td>
                              <Link to={`/tickets/${t.id}`}>{t.title}</Link>
                            </td>
                            <td><Badge color={t.severity === "Urgent" ? "danger" : t.severity === "High" ? "warning" : t.severity === "Medium" ? "warning" : "info"}>{t.severity}</Badge></td>
                            <td><Badge color={t.status === "Open" ? "primary" : t.status === "In Progress" ? "warning" : "success"}>{t.status}</Badge></td>
                            <td className="text-muted small">{t.propertyAddress || "—"}</td>
                            <td className="text-muted small">{t.createdAt ? t.createdAt.toLocaleDateString() : "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Delete Tenant Modal */}
        <Modal isOpen={deleteModal} toggle={() => setDeleteModal(false)}>
          <ModalHeader toggle={() => setDeleteModal(false)}>Delete Tenant</ModalHeader>
          <ModalBody>
            Delete <strong>{tenant.name || tenant.email}</strong>? Their portal access will be revoked immediately.
            You can restore them from the Tenants list at any time.
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={() => setDeleteModal(false)}>Cancel</Button>
            <Button color="danger" disabled={deleting} onClick={handleDelete}>
              {deleting ? <Spinner size="sm" /> : "Delete"}
            </Button>
          </ModalFooter>
        </Modal>

      </Container>
    </div>
  );
};

export default TenantDetail;
